import { useAppSelector } from '@store/hooks';
import { selectTheme } from '@store/reducers/appSlice';
import { useEffect } from 'react';

/**
 * Theme color type definition
 */
type ThemeColors = {
  primary: string;
  background: string;
  surface: string;
};

/**
 * Theme color configuration for light and dark modes
 * These colors will be applied to the PWA status bar and system UI
 */
const THEME_COLORS = {
  light: {
    primary: '#DFE3F1', // Your brand purple for light mode
    background: '#DFE3F1', // Light background
    surface: '#f8f9fa', // Light surface color
  },
  dark: {
    primary: '#252E40', // Slightly lighter purple for dark mode
    background: '#252E40', // Dark background
    surface: '#2d2d2d', // Dark surface color
  },
} as const;

/**
 * Hook to dynamically update PWA theme colors based on the current app theme
 * This updates the meta theme-color tag and manifest theme dynamically
 */
export const useDynamicThemeColor = () => {
  const currentTheme = useAppSelector(selectTheme);

  useEffect(() => {
    const isDarkMode = currentTheme === 'dark';
    const themeColors = isDarkMode ? THEME_COLORS.dark : THEME_COLORS.light;

    // Update the meta theme-color tag for PWA status bar
    updateMetaThemeColor(themeColors.primary);

    // Update HTML color-scheme preference
    updateHtmlColorScheme(isDarkMode);

    // Update other theme-related meta tags
    updateThemeMetaTags(themeColors, isDarkMode);

    // Notify service worker about theme change if available
    notifyServiceWorkerThemeChange(currentTheme, themeColors);
  }, [currentTheme]);

  // Return current theme info for components that need it
  return {
    currentTheme,
    isDarkMode: currentTheme === 'dark',
    themeColors: currentTheme === 'dark' ? THEME_COLORS.dark : THEME_COLORS.light,
  };
};

/**
 * Updates the meta theme-color tag for PWA status bar color
 */
function updateMetaThemeColor(color: string) {
  // Find existing theme-color meta tag
  let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;

  if (!themeColorMeta) {
    // Create theme-color meta tag if it doesn't exist
    themeColorMeta = document.createElement('meta');
    themeColorMeta.name = 'theme-color';
    document.head.appendChild(themeColorMeta);
  }

  // Update the color
  themeColorMeta.content = color;
}

/**
 * Updates the HTML element's color-scheme CSS property
 * This tells the browser which color scheme the page prefers
 */
function updateHtmlColorScheme(isDark: boolean) {
  const htmlElement = document.documentElement;

  // Set the color-scheme CSS property on the html element
  // This helps browsers apply appropriate default styling and form controls
  htmlElement.style.colorScheme = isDark ? 'dark' : 'light';

  // Also set the color-scheme attribute for better CSS support
  htmlElement.setAttribute('data-color-scheme', isDark ? 'dark' : 'light');
}

/**
 * Updates additional theme-related meta tags for better PWA experience
 */
function updateThemeMetaTags(colors: ThemeColors, isDark: boolean) {
  // Update Apple status bar style based on theme
  let statusBarMeta = document.querySelector(
    'meta[name="apple-mobile-web-app-status-bar-style"]',
  ) as HTMLMetaElement;
  if (!statusBarMeta) {
    statusBarMeta = document.createElement('meta');
    statusBarMeta.name = 'apple-mobile-web-app-status-bar-style';
    document.head.appendChild(statusBarMeta);
  }

  // iOS Safari status bar style
  statusBarMeta.content = isDark ? 'black-translucent' : 'default';

  // Update Microsoft tile color for Windows
  let msTileColorMeta = document.querySelector(
    'meta[name="msapplication-TileColor"]',
  ) as HTMLMetaElement;
  if (!msTileColorMeta) {
    msTileColorMeta = document.createElement('meta');
    msTileColorMeta.name = 'msapplication-TileColor';
    document.head.appendChild(msTileColorMeta);
  }
  msTileColorMeta.content = colors.primary;
}

/**
 * Notifies the service worker about theme changes
 * This allows the service worker to update cached manifest or other theme-related resources
 */
function notifyServiceWorkerThemeChange(theme: string, colors: ThemeColors) {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'THEME_CHANGED',
      theme,
      colors,
      timestamp: Date.now(),
    });
  }
}

/**
 * Utility function to get theme colors without the hook (for non-React contexts)
 */
export const getThemeColors = (theme: string) => {
  return theme === 'dark' ? THEME_COLORS.dark : THEME_COLORS.light;
};

/**
 * Utility function to apply theme colors immediately (useful for initial load)
 */
export const applyThemeColors = (theme: string) => {
  const colors = getThemeColors(theme);
  const isDark = theme === 'dark';
  updateMetaThemeColor(colors.primary);
  updateHtmlColorScheme(isDark);
  updateThemeMetaTags(colors, isDark);
};
